import axios from "axios";

const BASE_URL = "https://api.emerald-system.id:11059/api";
const AUTH_TOKEN = "Basic YWRtaW46YWRtaW4xMjM="; // admin:admin123 base64 encoded

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Authorization: AUTH_TOKEN,
  },
});

export interface Hotel {
  hotel_id: number;
  NAME: string;
  address: string;
  primarycolor: string;
  secondarycolor: string;
  tertiary: string;
  url_banner: string;
  url_banner2?: string;
  url_banner3?: string;
  url_banner4?: string;
  url_banner5?: string;
  url_banner6?: string;
  url_banner7?: string;
  url_banner8?: string;
  termagreement?: string;
  cancelation?: string;
  paymentgate?: string;
  apikey?: string;
  url_ig?: string;
  url_facebook?: string;
  url_youtube?: string;
  url_tiktok?: string;
  url_linkedin?: string;
  whatsapp_hotel?: string;
  hotelfont?: string;
  markup?: number;
  bottomrate?: number;
  dynamic_rate?: string;
  point_refferal?: number;
  use_membership?: number;
  rekening?: string;
  bank?: string;
  name_of_company?: string;
  map?: string;
  banner_info1?: string;
  banner_info2?: string;
}

export interface RoomFacility {
  facility_id: number;
  name: string;
  description: string;
  url_pic1?: string;
  url_pic2?: string;
  url_pic3?: string;
}

export interface RoomInfo {
  room_id: number;
  room_type: string;
  capacity: number;
  size: string;
  description: string;
  bedtype: string;
  images: string[];
  facilities: (string | RoomFacility)[];
}

export interface RatePlan {
  color: string;
  ratecode: string;
  ratedesc: string;
  final_rate: number;
  net_final_rate: number;
  include_breakfast: number | boolean; // API can return 0/1 or boolean
  is_active: number;
}

export interface RoomAvailability {
  room_info: RoomInfo;
  availability: {
    status: string;
    dates: {
      date: string;
      final_rate: number;
      rooms_to_sell: number;
      include_breakfast: boolean;
      available_rates?: RatePlan[]; // New field for multiple rates per day
    }[];
  };
  available_rates?: RatePlan[]; // Top level rates summary
}

export interface AddonItem {
  id: number;
  itemcode: string;
  itemdesc: string;
  amountplus: number;
  amountnett: number;
  promo?: string | null;
  url_image?: string;
  description?: string;
}

export interface CreateReservationPayload {
  reservation_id: string;
  customer_id: string;
  check_in_date: string;
  check_out_date: string;
  booker_name: string;
  booker_phone: string;
  booker_email: string;
  rate_code?: string;
  note?: string;
  rooms: {
    room_id: number;
    number_of_rooms: number;
    include_breakfast: boolean;
    amount_rate: number;
    rate_code?: string;
  }[];
}

export interface CreatePaymentPayload {
  reservation_id: string;
  order_id?: string;
  amount: number;
  payment_method?: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  bank_code?: string;
  hotel_id?: number;
  paymentgate?: string;
}

export const HotelService = {
  getHotel: async (db: string, id: number) => {
    const response = await api.get<Hotel>(`/hotels/${id}`, {
      headers: { "X-Database": db },
    });
    return response.data;
  },

  checkAvailability: async (
    db: string,
    params: {
      checkIn: string;
      checkOut: string;
      adults: number;
      children: number;
      room: number;
    }
  ) => {
    const response = await api.get<{ data: RoomAvailability[] }>(
      "/rooms/available",
      {
        headers: { "X-Database": db },
        params,
      }
    );
    return response.data;
  },

  getItems: async (db: string) => {
    const response = await api.get<{ data: AddonItem[] }>("/items", {
      headers: { "X-Database": db },
    });
    return response.data;
  },

  createCustomer: async (
    db: string,
    payload: {
      customer_id: string;
      full_name: string;
      email: string;
      phone: string;
    }
  ) => {
    const response = await api.post("/customers", payload, {
      headers: { "X-Database": db },
    });
    return response.data;
  },

  createReservation: async (db: string, payload: CreateReservationPayload) => {
    // If we have multiple rooms, we need to handle reservation IDs to avoid duplicates.
    // However, the current API seems to take a single payload for the reservation.
    // If the API endpoint handles splitting internally, we just send it.
    // But if the User instruction implies WE need to generate unique IDs for each room insertion,
    // we might need to adjust how we call this.
    
    // The user input shows a raw SQL insert which failed because of duplicate PRIMARY KEY.
    // This suggests the backend might be trying to insert multiple rows (one per room) using the SAME reservation_id.
    
    // IF the API endpoint expects us to handle the loop:
    // We would need to change this method to loop through rooms.
    
    // BUT typically, a 'createReservation' endpoint takes one reservation object.
    // If the backend is naive and tries to insert N rows with the same ID, that's a backend bug.
    // OR, we are supposed to send an array of reservation objects?
    
    // Looking at the payload interface:
    // payload.rooms is an array.
    // So we are sending ONE request with multiple rooms.
    
    // If the backend implementation iterates over `payload.rooms` and tries to insert into `reservations` table
    // using `payload.reservation_id` for EACH row, and `reservation_id` is the Primary Key, then it fails.
    
    // Since we cannot change the backend code (it's an external API), 
    // we must assume we might need to send separate requests OR the backend expects unique IDs per room?
    // BUT the payload has ONE `reservation_id` field at the top level.
    
    // Let's look at the user request:
    // "buatkan agar tidak duplicate ya residnya harusnya jika multiple kamar harusnya RES-20260223-827-1,RES-20260223-827-2 jika multi room"
    
    // This implies we should probably split the request into multiple API calls if there are multiple rooms,
    // OR change the payload structure if the API supports it.
    // Given the current interface `CreateReservationPayload` has `rooms` array but single `reservation_id`,
    // it strongly suggests a single booking group.
    
    // However, if the SQL error is "Duplicate entry", the backend table likely has `reservation_id` as PK
    // and stores 1 row per room? That's a bad schema design for a "Reservation Group".
    // Usually you have `Reservations` (Group) -> `ReservationDetails` (Rooms).
    
    // If we MUST fix this from frontend:
    // We should loop through the rooms and call the API for each room with a unique ID.
    
    if (payload.rooms.length > 1) {
      // Split into multiple requests
      const promises = payload.rooms.map((room, index) => {
        const uniqueResId = `${payload.reservation_id}-${index + 1}`;
        const singleRoomPayload = {
          ...payload,
          reservation_id: uniqueResId,
          rooms: [room] // Send one room at a time
        };
        
        return api.post("/reservations", singleRoomPayload, {
          headers: { "X-Database": db },
        });
      });
      
      // Return all results
      return Promise.all(promises);
    } else {
      // Single room, standard flow
      const response = await api.post("/reservations", payload, {
        headers: { "X-Database": db },
      });
      return response.data;
    }
  },

  createPayment: async (db: string, payload: CreatePaymentPayload) => {
    const response = await api.post("/payments", payload, {
      headers: { "X-Database": db },
    });
    return response.data;
  },
};

export default api;
